import { useCallback, useEffect, useState } from "react";
import type { ShopQueueItemDto } from "@grizzly/shared";
import { api } from "../lib/apiClient.js";
import type { ApiClientError } from "../lib/apiClient.js";
import { PageHeader } from "../components/ui/card.js";
import { Card } from "../components/ui/card.js";
import { SelectInput } from "../components/ui/field.js";
import { EmptyState } from "../components/ui/states.js";
import { QueueList } from "../components/shopFloor/queueList.js";
import { OperationPanel } from "../components/shopFloor/operationPanel.js";
import { useHasPermission } from "../lib/session.js";
import { useI18n } from "../i18n/index.js";
import { HardHat } from "lucide-react";

export function ShopFloorPage() {
  const { t } = useI18n();
  const canReport = useHasPermission("shopfloor:report");
  const [workCenters, setWorkCenters] = useState<
    { id: string; code: string; name: string }[]
  >([]);
  const [workCenterId, setWorkCenterId] = useState("");
  const [queue, setQueue] = useState<ShopQueueItemDto[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadWorkCenters = useCallback(async () => {
    try {
      const res = await api.listShopWorkCenters();
      setWorkCenters(res.items);
    } catch (err) {
      setError((err as ApiClientError).message);
    }
  }, []);

  const loadQueue = useCallback(async () => {
    try {
      const res = await api.listShopQueue(workCenterId || undefined);
      setQueue(res.items);
      setError(null);
    } catch (err) {
      setError((err as ApiClientError).message);
    }
  }, [workCenterId]);

  useEffect(() => {
    void loadWorkCenters();
  }, [loadWorkCenters]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  return (
    <>
      <PageHeader
        title={t("nav.shopFloor")}
        description={canReport ? t("shopFloor.descReady") : t("shopFloor.descLive")}
      />
      {error ? (
        <p role="alert" className="mb-4 text-sm text-action-danger">
          {error}
        </p>
      ) : null}

      <Card className="mb-4">
        <div className="max-w-xs">
          <SelectInput
            label={t("common.fields.workCenter")}
            options={[
              { value: "", label: t("shopFloor.allWorkCenters") },
              ...workCenters.map((w) => ({ value: w.id, label: `${w.name}` })),
            ]}
            value={workCenterId}
            onChange={(e) => {
              setWorkCenterId(e.target.value);
              setSelectedId(null);
            }}
          />
        </div>
      </Card>

      <div className="grid gap-4 xl:grid-cols-2">
        <section aria-label={t("shopFloor.workQueue")}>
          {queue.length === 0 ? (
            <EmptyState
              icon={HardHat}
              title={t("shopFloor.emptyQueueTitle")}
              description={t("shopFloor.emptyQueueDesc")}
            />
          ) : (
            <QueueList items={queue} selectedId={selectedId} onSelect={setSelectedId} />
          )}
        </section>

        {selectedId ? (
          <OperationPanel
            operationId={selectedId}
            canReport={canReport}
            onChanged={() => void loadQueue()}
          />
        ) : (
          <EmptyState
            icon={HardHat}
            title={t("shopFloor.selectOperation")}
            description={t("shopFloor.selectOperationDesc")}
          />
        )}
      </div>
    </>
  );
}
